import { useMemo, useState } from "react";
import {
  Brush,
  Code2,
  Layers,
  Image,
  Sparkles,
  Type,
  BoxSelect,
  Move,
} from "lucide-react";
import BackgroundControls, {
  DEFAULT_BACKGROUND,
  type BackgroundConfig,
} from "./dss/background-controls";
import TypographyControls, {
  type TypographyConfig,
} from "./dss/typography-controls";
import BoxModelEditor, {
  type SpacingConfig,
} from "./dss/box-model-editor";
import BorderControls, {
  type BorderConfig,
} from "./dss/border-controls";
import BoxShadowBuilder from "./dss/box-shadow-builder";
import TextShadowBuilder from "./dss/text-shadow-builder";
import ResponsiveOverrides from "./dss/responsive-overrides";
import { AnimationControls } from "./dss/animation-controls";
import TransformBuilder, { type TransformConfig } from "./dss/transform-builder";
import FilterControls, { type FilterConfig } from "./dss/filter-controls";
import BlendModePicker from "./dss/blend-mode-picker";
import PositionControls, { type PositionConfig } from "./dss/position-controls";
import DisplayControl from "./dss/display-control";
import DeviceVisibilityControls, { type DeviceVisibility } from "./dss/device-visibility";
import { normalizeColorInputValue } from "./dss/color-input-value";
import CssIdClasses from "./dss/css-id-classes";
import CustomHtmlAttributes from "./dss/custom-html-attributes";
import StylePresets from "./dss/style-presets";
import StateTabs from "./state-tabs";
import { StylePanelSection } from "./style-panel-section";
import { useStylePanel } from "./style-panel-context";
import {
  sanitizeClassNames,
  sanitizeCustomHtmlAttributes,
  sanitizeElementId,
  type BlockStyleData,
} from "./style-state-system";

type TabId = "content" | "style" | "advanced";

const TABS: Array<{ id: TabId; label: string; icon: React.ComponentType<{ size?: number }> }> = [
  { id: "content", label: "Select", icon: Layers },
  { id: "style", label: "Style", icon: Brush },
  { id: "advanced", label: "Safe", icon: Code2 },
];

const DEFAULT_TYPOGRAPHY: TypographyConfig = {
  fontFamily: "Inter",
  fontSize: 16,
  fontSizeUnit: "px",
  fontWeight: 400,
  fontStyle: "normal",
  textTransform: "none",
  textDecoration: "none",
  lineHeight: 1.5,
  lineHeightUnit: "",
  letterSpacing: 0,
  letterSpacingUnit: "px",
  wordSpacing: 0,
};

const DEFAULT_SPACING: SpacingConfig = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  unit: "px",
};

const DEFAULT_BORDER: BorderConfig = {
  style: "none",
  widthTop: 0,
  widthRight: 0,
  widthBottom: 0,
  widthLeft: 0,
  isWidthLinked: true,
  color: "#d1d5db",
  radiusTopLeft: 0,
  radiusTopRight: 0,
  radiusBottomRight: 0,
  radiusBottomLeft: 0,
  isRadiusLinked: true,
};

const DEFAULT_TRANSFORM: TransformConfig = {
  translateX: 0,
  translateXUnit: "px",
  translateY: 0,
  translateYUnit: "px",
  rotate: 0,
  is3d: false,
  rotateX: 0,
  rotateY: 0,
  rotateZ: 0,
  perspective: 1000,
  scaleX: 100,
  scaleY: 100,
  skewX: 0,
  skewY: 0,
  flipH: false,
  flipV: false,
  originX: "center",
  originY: "center",
  state: "normal",
};

const DEFAULT_FILTER: FilterConfig = {
  blur: 0,
  brightness: 100,
  contrast: 100,
  hueRotate: 0,
  saturate: 100,
  grayscale: 0,
  invert: 0,
  sepia: 0,
  opacity: 100,
  blendMode: "normal",
  state: "normal",
};

const DEFAULT_POSITION: PositionConfig = {
  position: "static",
  top: null,
  right: null,
  bottom: null,
  left: null,
  zIndex: 0,
  overflow: "visible",
};

const DEFAULT_VISIBILITY: DeviceVisibility = {
  hideDesktop: false,
  hideTablet: false,
  hideMobile: false,
};

function updateStyleState(
  style: BlockStyleData | undefined,
  patch: Partial<BlockStyleData>,
): BlockStyleData {
  return {
    ...(style ?? {}),
    ...patch,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label
      style={{
        display: "block",
        fontSize: "10px",
        fontWeight: 600,
        color: "var(--color-muted-foreground)",
        marginBottom: "4px",
      }}
    >
      {children}
    </label>
  );
}

function TextMetaControls({
  color,
  align,
  onColorChange,
  onAlignChange,
}: {
  color: string;
  align: "left" | "center" | "right" | "justify";
  onColorChange: (value: string) => void;
  onAlignChange: (value: "left" | "center" | "right" | "justify") => void;
}) {
  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
      <div>
        <FieldLabel>Text Color</FieldLabel>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={normalizeColorInputValue(color)}
            onChange={(event) => onColorChange(event.target.value)}
            className="h-7 w-10 cursor-pointer rounded border border-[var(--color-border)] bg-transparent p-0"
          />
          <code className="text-[10px] text-[var(--color-muted-foreground)]">{color}</code>
        </div>
      </div>

      <div>
        <FieldLabel>Text Align</FieldLabel>
        <div className="grid grid-cols-4 gap-1">
          {(["left", "center", "right", "justify"] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onAlignChange(option)}
              className={[
                "rounded-md border px-2 py-1 text-[10px] font-medium capitalize transition-colors",
                align === option
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                  : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/50",
              ].join(" ")}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function StylePanel() {
  const [activeTab, setActiveTab] = useState<TabId>("style");
  const {
    blocks,
    selectedComponentId,
    selectedComponentType,
    setSelectedComponentId,
    activeState,
    setActiveState,
    getSelectedBlockStyles,
    getSelectedStyleState,
    setSelectedStyleState,
    replaceSelectedDssStyles,
    copiedDssStyles,
    copySelectedDssStyles,
    pasteCopiedDssStyles,
    getSelectedResponsiveConfig,
    setSelectedResponsiveConfig,
    getSelectedComponentProps,
  } = useStylePanel();

  const currentStyle = getSelectedStyleState();
  const selectedStates = getSelectedBlockStyles();
  const background = currentStyle?.background ?? null;
  const typography = currentStyle?.typography ?? DEFAULT_TYPOGRAPHY;
  const spacing = currentStyle?.spacing ?? {
    padding: DEFAULT_SPACING,
    margin: DEFAULT_SPACING,
  };
  const border = currentStyle?.border ?? DEFAULT_BORDER;
  const shadow = currentStyle?.shadow ?? "none";
  const textShadow = currentStyle?.textShadow ?? "none";
  const transform = currentStyle?.transform ?? DEFAULT_TRANSFORM;
  const filter = currentStyle?.filter ?? DEFAULT_FILTER;
  const blendMode = currentStyle?.blendMode ?? filter.blendMode ?? "normal";
  const positionConfig = currentStyle?.positionConfig ?? DEFAULT_POSITION;
  const display = currentStyle?.display ?? "block";
  const visibility = currentStyle?.visibility ?? DEFAULT_VISIBILITY;
  const animation = currentStyle?.animation;
  const elementId = currentStyle?.elementId ?? "";
  const classNames = currentStyle?.classNames ?? [];
  const customAttributes = currentStyle?.customAttributes ?? [];
  const textColor = currentStyle?.text?.color ?? "#111827";
  const textAlign = currentStyle?.text?.align ?? "left";
  const responsive = getSelectedResponsiveConfig() ?? {
    desktop: {},
    tablet: {},
    mobile: {},
  };
  const selectedComponentProps = getSelectedComponentProps();
  const presetStyleData = isRecord(selectedComponentProps?._dssStyles)
    ? { ...selectedComponentProps._dssStyles }
    : null;

  const stateOverrides = useMemo(
    () => ({
      _hoverStyle: selectedStates.hover ? { configured: "1" } : undefined,
      _focusStyle: selectedStates.focus ? { configured: "1" } : undefined,
      _activeStyle: selectedStates.active ? { configured: "1" } : undefined,
    }),
    [selectedStates.active, selectedStates.focus, selectedStates.hover],
  );

  const hasSelection = selectedComponentId !== null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        background: "var(--color-background)",
      }}
    >
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid var(--color-border)",
          flexShrink: 0,
        }}
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                padding: "8px 4px",
                border: "none",
                borderBottom: isActive ? "2px solid var(--color-primary)" : "2px solid transparent",
                background: "transparent",
                color: isActive ? "var(--color-primary)" : "var(--color-muted-foreground)",
                fontSize: "11px",
                fontWeight: isActive ? 600 : 400,
                cursor: "pointer",
              }}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {selectedComponentId ? (
        <div
          style={{
            padding: "6px 12px",
            fontSize: "11px",
            color: "var(--color-muted-foreground)",
            borderBottom: "1px solid var(--color-border)",
            flexShrink: 0,
          }}
        >
          Editing:{" "}
          <strong style={{ color: "var(--color-foreground)" }}>
            {selectedComponentType ?? "component"} [{selectedComponentId}]
          </strong>
        </div>
      ) : null}

      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {activeTab === "content" ? (
          <div style={{ padding: "8px 12px", display: "flex", flexDirection: "column", gap: "8px" }}>
            {blocks.length === 0 ? (
              <p style={{ margin: 0, fontSize: "12px", color: "var(--color-muted-foreground)" }}>
                Add a block in the canvas to start styling it.
              </p>
            ) : (
              blocks.map((block, index) => {
                const isSelected = block.id === selectedComponentId;
                return (
                  <button
                    key={block.id}
                    type="button"
                    onClick={() => setSelectedComponentId(block.id)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: isSelected
                        ? "1px solid var(--color-primary)"
                        : "1px solid var(--color-border)",
                      background: isSelected ? "rgba(37, 99, 235, 0.08)" : "var(--color-card)",
                      color: "var(--color-foreground)",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span style={{ fontSize: "12px", fontWeight: 600 }}>
                      {index + 1}. {block.type}
                    </span>
                    <code style={{ fontSize: "10px", color: "var(--color-muted-foreground)" }}>
                      {block.id}
                    </code>
                  </button>
                );
              })
            )}
          </div>
        ) : null}

        {activeTab === "style" ? (
          <div>
            <div style={{ padding: "12px" }}>
              <StateTabs
                activeState={activeState}
                onStateChange={setActiveState}
                overrides={stateOverrides}
              />
            </div>

            {!hasSelection ? (
              <p style={{ padding: "0 12px 12px", margin: 0, fontSize: "12px", color: "var(--color-muted-foreground)" }}>
                Select a block in the canvas or Layers rail to edit its published styles.
              </p>
            ) : (
              <>
                {activeState === "normal" ? (
                  <StylePanelSection id="style-clipboard" label="Style Clipboard" icon={Layers}>
                    <div className="flex flex-col gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/30 p-3">
                      <div className="text-[10px] text-[var(--color-muted-foreground)]">
                        Copy the selected block&apos;s canonical styles, then select another block and paste them through the same `_dssStyles` contract used by the renderer.
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={copySelectedDssStyles}
                          disabled={!presetStyleData}
                          className="rounded-md border border-[var(--color-border)] px-3 py-2 text-[11px] font-medium text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:text-[var(--color-muted-foreground)]"
                        >
                          Copy Style
                        </button>
                        <button
                          type="button"
                          onClick={pasteCopiedDssStyles}
                          disabled={!copiedDssStyles}
                          className="rounded-md border border-[var(--color-border)] px-3 py-2 text-[11px] font-medium text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:text-[var(--color-muted-foreground)]"
                        >
                          Paste Style
                        </button>
                      </div>
                      <div className="text-[10px] text-[var(--color-muted-foreground)]">
                        {copiedDssStyles ? "Clipboard ready." : "No copied style yet."}
                      </div>
                    </div>
                  </StylePanelSection>
                ) : null}

                {activeState === "normal" ? (
                  <StylePanelSection id="style-presets" label="Style Presets" icon={Sparkles}>
                    <StylePresets
                      value={presetStyleData}
                      onApply={(styleData) => {
                        replaceSelectedDssStyles(styleData);
                      }}
                    />
                  </StylePanelSection>
                ) : null}

                <StylePanelSection id="background" label="Background" icon={Image}>
                  <BackgroundControls
                    value={background}
                    onChange={(nextBackground) => {
                      setSelectedStyleState(
                        updateStyleState(currentStyle, {
                          background: nextBackground ?? undefined,
                        }),
                      );
                    }}
                  />
                </StylePanelSection>

                <StylePanelSection id="typography" label="Typography" icon={Type}>
                  <div className="flex flex-col gap-3">
                    <TextMetaControls
                      color={textColor}
                      align={textAlign}
                      onColorChange={(value) => {
                        setSelectedStyleState(
                          updateStyleState(currentStyle, {
                            text: {
                              ...(currentStyle?.text ?? {}),
                              color: value,
                            },
                          }),
                        );
                      }}
                      onAlignChange={(value) => {
                        setSelectedStyleState(
                          updateStyleState(currentStyle, {
                            text: {
                              ...(currentStyle?.text ?? {}),
                              align: value,
                            },
                          }),
                        );
                      }}
                    />
                    <TypographyControls
                      value={typography}
                      onChange={(nextTypography) => {
                        setSelectedStyleState(
                          updateStyleState(currentStyle, {
                            typography: nextTypography,
                          }),
                        );
                      }}
                    />
                  </div>
                </StylePanelSection>

                <StylePanelSection id="border-shadow" label="Border & Shadow" icon={BoxSelect}>
                  <div className="flex flex-col gap-3">
                    <BorderControls
                      value={border}
                      onChange={(nextBorder) => {
                        setSelectedStyleState(
                          updateStyleState(currentStyle, {
                            border: nextBorder,
                          }),
                        );
                      }}
                    />
                    <BoxShadowBuilder
                      value={shadow}
                      onChange={(nextShadow) => {
                        setSelectedStyleState(
                          updateStyleState(currentStyle, {
                            shadow: nextShadow,
                          }),
                        );
                      }}
                    />
                    <TextShadowBuilder
                      value={textShadow}
                      onChange={(nextTextShadow) => {
                        setSelectedStyleState(
                          updateStyleState(currentStyle, {
                            textShadow: nextTextShadow,
                          }),
                        );
                      }}
                    />
                  </div>
                </StylePanelSection>

                <StylePanelSection id="transform-filters" label="Transform & Filters" icon={Sparkles}>
                  <div className="flex flex-col gap-3">
                    <TransformBuilder
                      value={transform}
                      onChange={(nextTransform) => {
                        setSelectedStyleState(
                          updateStyleState(currentStyle, {
                            transform: nextTransform,
                          }),
                        );
                      }}
                    />
                    <FilterControls
                      value={{ ...filter, blendMode }}
                      onChange={(nextFilter) => {
                        setSelectedStyleState(
                          updateStyleState(currentStyle, {
                            filter: nextFilter,
                            blendMode: nextFilter.blendMode,
                          }),
                        );
                      }}
                    />
                    <BlendModePicker
                      value={blendMode}
                      onChange={(nextBlendMode) => {
                        setSelectedStyleState(
                          updateStyleState(currentStyle, {
                            blendMode: nextBlendMode,
                            filter: {
                              ...filter,
                              blendMode: nextBlendMode,
                            },
                          }),
                        );
                      }}
                    />
                  </div>
                </StylePanelSection>

                <StylePanelSection id="spacing" label="Spacing" icon={Move}>
                  <BoxModelEditor
                    padding={spacing.padding}
                    margin={spacing.margin}
                    onPaddingChange={(padding) => {
                      setSelectedStyleState(
                        updateStyleState(currentStyle, {
                          spacing: {
                            padding,
                            margin: spacing.margin,
                          },
                        }),
                      );
                    }}
                    onMarginChange={(margin) => {
                      setSelectedStyleState(
                        updateStyleState(currentStyle, {
                          spacing: {
                            padding: spacing.padding,
                            margin,
                          },
                        }),
                      );
                    }}
                  />
                </StylePanelSection>

                <StylePanelSection id="animation" label="Animation" icon={Sparkles}>
                  <AnimationControls
                    value={animation}
                    onChange={(nextAnimation) => {
                      setSelectedStyleState(
                        updateStyleState(currentStyle, {
                          animation: nextAnimation,
                        }),
                      );
                    }}
                  />
                </StylePanelSection>

                {activeState === "normal" ? (
                  <StylePanelSection id="responsive" label="Responsive" icon={Layers}>
                    <ResponsiveOverrides
                      value={responsive}
                      onChange={setSelectedResponsiveConfig}
                    />
                  </StylePanelSection>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        {activeTab === "advanced" ? (
          <div>
            {!hasSelection ? (
              <p style={{ padding: "12px", margin: 0, fontSize: "12px", color: "var(--color-muted-foreground)" }}>
                Select a block in the canvas or Blocks tab to edit advanced behavior.
              </p>
            ) : (
              <>
                <StylePanelSection id="position" label="Position" icon={Move}>
                  <PositionControls
                    value={positionConfig}
                    onChange={(nextPosition) => {
                      setSelectedStyleState(
                        updateStyleState(currentStyle, {
                          positionConfig: nextPosition,
                        }),
                      );
                    }}
                  />
                </StylePanelSection>

                <StylePanelSection id="display" label="Display" icon={Layers}>
                  <DisplayControl
                    value={display}
                    onChange={(nextDisplay) => {
                      setSelectedStyleState(
                        updateStyleState(currentStyle, {
                          display: nextDisplay,
                        }),
                      );
                    }}
                  />
                </StylePanelSection>

                {activeState === "normal" ? (
                  <StylePanelSection id="device-visibility" label="Device Visibility" icon={Image}>
                    <DeviceVisibilityControls
                      value={visibility}
                      onChange={(nextVisibility) => {
                        setSelectedStyleState(
                          updateStyleState(currentStyle, {
                            visibility: nextVisibility,
                          }),
                        );
                      }}
                    />
                  </StylePanelSection>
                ) : null}

                {activeState === "normal" ? (
                  <StylePanelSection id="css-id-classes" label="CSS ID + Classes" icon={Code2}>
                    <CssIdClasses
                      value={{ elementId, classNames }}
                      onChange={(nextConfig) => {
                        setSelectedStyleState(
                          updateStyleState(currentStyle, {
                            elementId: sanitizeElementId(nextConfig.elementId),
                            classNames: sanitizeClassNames(nextConfig.classNames),
                          }),
                        );
                      }}
                    />
                  </StylePanelSection>
                ) : null}

                {activeState === "normal" ? (
                  <StylePanelSection id="custom-html-attributes" label="Custom HTML Attributes" icon={Code2}>
                    <CustomHtmlAttributes
                      value={customAttributes}
                      onChange={(nextAttributes) => {
                        setSelectedStyleState(
                          updateStyleState(currentStyle, {
                            customAttributes: sanitizeCustomHtmlAttributes(nextAttributes),
                          }),
                        );
                      }}
                    />
                  </StylePanelSection>
                ) : null}

                <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/30 p-3">
                    <p style={{ margin: 0, fontSize: "12px", lineHeight: "18px", color: "var(--color-muted-foreground)" }}>
                      Published pages only receive hashed, generated style rules from this panel. No raw custom CSS is injected into SSR output.
                    </p>
                  </div>
                  <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/30 p-3">
                    <p style={{ margin: 0, fontSize: "12px", lineHeight: "18px", color: "var(--color-muted-foreground)" }}>
                      The active style state is persisted on the selected block and rendered identically in editor preview and published HTML.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
